import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

interface CopyButtonProps {
  /** The exact string written to the clipboard. */
  value: string;
  /** Optional friendly label included in the toast ("Copied claim CLM-…"). */
  label?: string;
  className?: string;
  /** When true, inherits color from the parent (e.g. inside the dark
   *  SectionBar header). Otherwise uses muted-foreground. */
  inverse?: boolean;
}

// Small icon button that copies `value` to the clipboard, fires a toast, and
// briefly swaps the icon to a checkmark for visual confirmation. Falls back
// silently when navigator.clipboard is unavailable (older browsers / insecure
// origins) — we surface a toast.error and log so we still know it happened.
export function CopyButton({ value, label, className, inverse = false }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1200);
    return () => clearTimeout(t);
  }, [copied]);

  async function handleCopy(e: React.MouseEvent) {
    // SectionBar wraps its title in a <Link>; stop the click from bubbling
    // into the navigation handler so copying doesn't also navigate away.
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(label ? `Copied ${label}` : "Copied to clipboard");
    } catch (err) {
      logger.error("clipboard_copy_failed", { err });
      toast.error("Couldn't copy to clipboard");
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={label ? `Copy ${label}` : "Copy"}
      title={label ? `Copy ${label}` : "Copy"}
      className={cn(
        "inline-flex size-6 shrink-0 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
        inverse
          ? "text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
        className,
      )}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
}
